import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { modulesForRole } from "@/constants/modules";
import { ChevronDown, LogOut, Menu, X, Building2, Layers } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const { allSites, activeSite, setActiveSite, current } = useSite();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const mods = modulesForRole(user?.role);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen bg-[#f5f6f7]">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform border-r border-[#e5e9f0] bg-white transition-transform duration-300 lg:static lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-14 items-center gap-2.5 border-b border-[#e5e9f0] px-4">
          <div className="flex h-9 items-center rounded-md bg-[#354a5f] px-2.5">
            <img src="/epgroup-logo-white.gif" alt="EP Group" className="h-4 w-auto" />
          </div>
          <div className="text-[10px] uppercase leading-tight tracking-wider text-[#6a6d70]">Management<br />System</div>
        </div>
        <nav className="flex flex-col gap-0.5 p-2 overflow-y-auto" style={{ maxHeight: "calc(100vh - 3.5rem)" }}>
          {mods.map((m) => (
            <NavLink
              key={m.key}
              to={m.path}
              end={m.path === "/"}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${m.key}`}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-all ${
                  isActive ? "bg-[#ebf5fe] font-medium text-[#0a6ed1]" : "text-[#4a4f54] hover:bg-[#f5f6f7]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r bg-[#0a6ed1]" />}
                  <m.icon size={18} style={{ color: m.color }} />
                  <span className="flex-1">{m.label}</span>
                  <span className="text-[10px] font-mono rounded px-1.5 py-0.5"
                        style={{ background: `${m.color}18`, color: m.color }}>{m.short}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      {mobileOpen && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 bg-[#354a5f] px-4 text-white shadow-sm">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-white/90" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <img src="/epgroup-logo-white.gif" alt="EP Group" className="hidden sm:block h-5 w-auto" />
            {/* Site selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="plant-site-selector"
                        className="flex items-center gap-2 rounded-md bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/15">
                  {activeSite === "GROUP" ? <Layers size={16} className="text-amber-300" /> : <Building2 size={16} className="text-sky-300" />}
                  <div className="text-left leading-none">
                    <div className="font-medium">{current.name}</div>
                    <div className="text-[10px] text-white/60">PLANT {current.code}</div>
                  </div>
                  <ChevronDown size={15} className="text-white/60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 border-[#e5e9f0] bg-white text-[#32363a]">
                <DropdownMenuLabel className="text-[10px] uppercase text-[#6a6d70]">Select Site Scope</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#e5e9f0]" />
                {allSites.map((s) => (
                  <DropdownMenuItem key={s.id} data-testid={`site-option-${s.id}`}
                    onClick={() => setActiveSite(s.id)}
                    className="cursor-pointer focus:bg-[#ebf5fe] focus:text-[#0a6ed1]">
                    {s.id === "GROUP" ? <Layers size={15} className="mr-2 text-[#e9730c]" /> : <Building2 size={15} className="mr-2 text-[#0a6ed1]" />}
                    <span className="flex-1">{s.name}</span>
                    {activeSite === s.id && <span className="h-2 w-2 rounded-full bg-[#0a6ed1]" />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* User */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn"
                      className="flex items-center gap-2.5 rounded-md px-2 py-1 hover:bg-white/10">
                <div className="hidden text-right sm:block leading-none">
                  <div className="text-sm font-medium text-white">{user?.name}</div>
                  <div className="text-[10px] text-white/60">{user?.role_label}</div>
                </div>
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0a6ed1] text-sm font-semibold text-white">
                  {user?.name?.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 border-[#e5e9f0] bg-white text-[#32363a]">
              <DropdownMenuLabel>
                <div className="text-sm">{user?.name}</div>
                <div className="text-[10px] text-[#6a6d70]">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#e5e9f0]" />
              <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout}
                className="cursor-pointer text-[#bb0000] focus:bg-[#fbeaea] focus:text-[#bb0000]">
                <LogOut size={15} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
